/**
 * La scheda di un'azienda scelta sulla mappa. In cima il suo marchio: una fascia del suo colore, il logo su una tessera
 * bianca e il nome. Sotto i fatti, uno per riga: iscrizione, account per livello, persone al lavoro, ultimo utilizzo,
 * le sedi (toccandone una la carta ci va) e, solo nella console, posti, abbonamento, stato, gli indirizzi delle sedi e
 * il passaggio alla sua scheda completa.
 */
import React from 'react';
import { Box, Button, ButtonBase, IconButton, Tooltip, Typography, alpha } from '@mui/material';
import { ArrowBack, DesktopWindowsOutlined, OpenInNew, PhoneAndroidOutlined, Place } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { LIVELLI_MAPPA, type LivelloMappa, type PersonaAlLavoro, type PersonaConLaPaginaAperta } from '@shared/admin/mappaAziende';
import { tokens } from '../../theme';
import { TierBadge } from '../design/TierBadge';
import { SoftChip } from '../SoftChip';
import { COLORE_LIVELLO, COLORI_MAPPA } from './aspetto';
import type { FormatiMappa } from './formati';
import { LogoAzienda } from './LogoAzienda';
import { BarraDeiLivelli } from './PannelloRiepilogo';
import { PallinoAdesso } from './PallinoAdesso';
import type { AziendaNellaVista, ModoMappa } from './tipi';

interface Props {
  azienda: AziendaNellaVista;
  modo: ModoMappa;
  giorni: number;
  formati: FormatiMappa;
  onTorna: () => void;
  onApri?: (tenantId: string) => void;
  /** Porta la carta su una sede dell'azienda. */
  onVaiAllaSede?: (chiaveSede: string) => void;
  /** Chi la rifornisce di materiale e chi rifornisce lei (solo console). */
  fornitori?: RiferimentoAzienda[];
  clienti?: RiferimentoAzienda[];
  /** Chi l'ha portata su YumaOS (programma partner) e le aziende che ha portato il suo partner (solo console). */
  portataDa?: { partner: string; azienda: RiferimentoAzienda | null } | null;
  portate?: RiferimentoAzienda[];
  onScegliAzienda?: (chiave: string) => void;
}

export interface RiferimentoAzienda {
  chiave: string;
  nome: string;
  /** Sta sulla carta coi filtri di adesso (si può scegliere). */
  inVista: boolean;
}

/** Un elenco di aziende che si toccano per sceglierle sulla carta. */
const ElencoRiferimenti: React.FC<{ aziende: RiferimentoAzienda[]; onScegli?: (chiave: string) => void }> = ({ aziende, onScegli }) => {
  const { t } = useTranslation('mappa');
  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {aziende.map((a) => (
        <Box component="li" key={a.chiave}>
          <ButtonBase
            disabled={!a.inVista || !onScegli}
            onClick={() => onScegli?.(a.chiave)}
            sx={{
              width: '100%', justifyContent: 'flex-start', textAlign: 'left', borderRadius: 1, py: 0.5, px: 0.75, mx: -0.75, minHeight: 36,
              '&:hover': { bgcolor: tokens.color.bone },
              '&.Mui-focusVisible': { outline: `2px solid ${tokens.color.graphite}`, outlineOffset: -2 },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, lineHeight: 1.35, color: a.inVista ? tokens.color.graphite : tokens.color.ash }}>{a.nome}</Typography>
              {!a.inVista && <Typography sx={{ fontSize: '0.72rem', color: tokens.color.mist }}>{t('scheda.fuori_dai_filtri')}</Typography>}
            </Box>
          </ButtonBase>
        </Box>
      ))}
    </Box>
  );
};

const TONO_ABBONAMENTO = {
  esente: 'info',
  in_prova: 'info',
  pagante: 'success',
  insoluto: 'warning',
  cancellato: 'error',
  gratuito: 'default',
} as const;

const Fatto: React.FC<{ etichetta: string; children: React.ReactNode; sotto?: React.ReactNode }> = ({ etichetta, children, sotto }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(108px, 40%) 1fr',
      gap: 1.5,
      py: 1.25,
      borderTop: `1px solid ${tokens.color.lineSoft}`,
      alignItems: 'baseline',
    }}
  >
    <Typography component="dt" sx={{ fontSize: '0.78rem', color: tokens.color.ash, lineHeight: 1.4 }}>{etichetta}</Typography>
    <Box component="dd" sx={{ m: 0, fontSize: '0.88rem', color: tokens.color.graphite, lineHeight: 1.4, minWidth: 0 }}>{children}</Box>
    {/* Un elenco che nella colonna stretta si taglierebbe: sotto, a tutta larghezza. */}
    {sotto && <Box component="dd" sx={{ m: 0, gridColumn: '1 / -1', minWidth: 0 }}>{sotto}</Box>}
  </Box>
);

const iniziali = (nome: string) => {
  const parole = nome.trim().split(/\s+/).filter(Boolean);
  const prima = parole[0]?.charAt(0) ?? '';
  const ultima = parole.length > 1 ? parole[parole.length - 1].charAt(0) : '';
  return (prima + ultima).toUpperCase();
};

/**
 * Una persona in un elenco della scheda: le iniziali (col pallino dello stato, se c'è), il nome intero (va a capo, non
 * si taglia), sotto l'email dell'account (distingue un account d'ufficio come «Ufficio tecnico SSG» da una persona) e
 * una riga di dettaglio; a destra, se serve, da dove lavora.
 */
const RigaPersona: React.FC<{ nome: string; email: string | null; pallino: string | null; dettaglio?: string; destra?: React.ReactNode }> = ({
  nome, email, pallino, dettaglio, destra,
}) => (
  <Box component="li" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.9, '& + &': { borderTop: `1px dashed ${tokens.color.lineSoft}` } }}>
    <Box sx={{ position: 'relative', flexShrink: 0 }}>
      <Box
        aria-hidden
        sx={{
          width: 32, height: 32, borderRadius: '50%', display: 'grid', placeItems: 'center',
          bgcolor: tokens.color.bone, border: `1px solid ${tokens.color.line}`,
          fontSize: '0.7rem', fontWeight: 700, color: tokens.color.ash, letterSpacing: '0.02em',
        }}
      >
        {iniziali(nome)}
      </Box>
      {pallino && <Box aria-hidden sx={{ position: 'absolute', right: -1, bottom: -1, width: 10, height: 10, borderRadius: '50%', bgcolor: pallino, border: `2px solid ${tokens.color.paper}` }} />}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, lineHeight: 1.3, color: tokens.color.graphite, overflowWrap: 'anywhere' }}>{nome}</Typography>
      {email && <Typography sx={{ fontSize: '0.74rem', color: tokens.color.ash, lineHeight: 1.35, overflowWrap: 'anywhere' }}>{email}</Typography>}
      {dettaglio && <Typography sx={{ fontSize: '0.74rem', color: tokens.color.ash, lineHeight: 1.35 }}>{dettaglio}</Typography>}
    </Box>
    {destra && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0, alignItems: 'flex-end' }}>{destra}</Box>}
  </Box>
);

/** Chi sta lavorando adesso, una persona per riga, con da dove lavora (gestionale, app). */
const ChiLavoraAdesso: React.FC<{ persone: PersonaAlLavoro[] }> = ({ persone }) => {
  const { t } = useTranslation('mappa');
  return (
    <Box component="ul" aria-label={t('scheda.chi_lavora_adesso')} sx={{ listStyle: 'none', m: 0, mt: 0.5, p: 0 }}>
      {persone.map((p, i) => (
        <RigaPersona
          key={`${p.email ?? p.nome}-${i}`}
          nome={p.nome}
          email={p.email}
          pallino={COLORI_MAPPA.adesso}
          destra={p.dove.map((d) => (
            <Tooltip key={d} title={t(`scheda.canale_aiuto.${d}`)}>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.9, py: 0.25, borderRadius: `${tokens.radius.pill}px`,
                  border: `1px solid ${tokens.color.line}`, fontSize: '0.7rem', fontWeight: 600, color: tokens.color.ash, whiteSpace: 'nowrap',
                }}
              >
                {d === 'app' ? <PhoneAndroidOutlined sx={{ fontSize: 13 }} /> : <DesktopWindowsOutlined sx={{ fontSize: 13 }} />}
                {t(`scheda.canale.${d}`)}
              </Box>
            </Tooltip>
          ))}
        />
      ))}
    </Box>
  );
};

/**
 * Chi ha il gestionale aperto ma non lo usa (nessuna azione negli ultimi minuti, `statoDiPresenza`): senza pallino
 * verde, con l'ora dell'ultima azione. Un gestionale ancora vecchio non la manda: lo si dice, non si inventa.
 */
const ChiHaLaPaginaAperta: React.FC<{ persone: PersonaConLaPaginaAperta[]; quando: (iso: string) => string }> = ({ persone, quando }) => {
  const { t } = useTranslation('mappa');
  return (
    <Box component="ul" aria-label={t('scheda.chi_ha_la_pagina_aperta')} sx={{ listStyle: 'none', m: 0, mt: 0.5, p: 0 }}>
      {persone.map((p, i) => (
        <RigaPersona
          key={`${p.email ?? p.nome}-${i}`}
          nome={p.nome}
          email={p.email}
          pallino={null}
          dettaglio={p.ultima_azione_il ? t('scheda.ultima_azione', { quando: quando(p.ultima_azione_il) }) : t('scheda.ultima_azione_non_misurata')}
        />
      ))}
    </Box>
  );
};

/** Le persone al lavoro su tutti gli account: una barra sottile, il numero accanto. */
const Partecipazione: React.FC<{ attive: number; totale: number; colore: string; formati: FormatiMappa }> = ({ attive, totale, colore, formati }) => {
  const { t } = useTranslation('mappa');
  const quota = totale > 0 ? Math.min(1, attive / totale) : 0;
  return (
    <Box>
      <Typography component="span" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {t('scheda.attive_su', { attive: formati.numero(attive), totale: formati.numero(totale) })}
      </Typography>
      <Box sx={{ mt: 0.75, height: 6, borderRadius: 3, bgcolor: tokens.color.lineSoft, overflow: 'hidden' }} aria-hidden>
        <Box sx={{ width: `${quota * 100}%`, height: '100%', bgcolor: colore, borderRadius: 3 }} />
      </Box>
    </Box>
  );
};

export const SchedaAzienda: React.FC<Props> = ({
  azienda, modo, giorni, formati, onTorna, onApri, onVaiAllaSede, fornitori = [], clienti = [], portataDa = null, portate = [], onScegliAzienda,
}) => {
  const { t } = useTranslation('mappa');
  const marchio = azienda.colore ?? COLORE_LIVELLO[azienda.livello];
  const perLivello = Object.fromEntries(LIVELLI_MAPPA.map((l) => [l, azienda.account[l] ?? 0])) as Record<LivelloMappa, number>;
  /** «oggi alle 18:50», «ieri alle 9:12», «martedì alle 10:03», «13 ottobre 2026». */
  const quando = (iso: string | null): string | null => {
    const momento = formati.momento(iso);
    if (!momento) return null;
    if (momento.giorno === 'oggi') return t('scheda.ultimo_uso_oggi', { ora: momento.ora });
    if (momento.giorno === 'ieri') return t('scheda.ultimo_uso_ieri', { ora: momento.ora });
    if (momento.giorno === 'settimana') return t('scheda.ultimo_uso_settimana', { giorno: momento.nomeGiorno, ora: momento.ora });
    return t('scheda.ultimo_uso_prima', { data: momento.data });
  };
  const ultimoUso = quando(azienda.ultima_attivita_il) ?? t('scheda.ultimo_uso_mai');
  const daConsole = modo === 'console';
  const luogo = [azienda.citta, azienda.provincia ? `(${azienda.provincia})` : null].filter(Boolean).join(' ');

  return (
    <Box>
      {/* Il marchio dell'azienda */}
      <Box sx={{ position: 'relative', bgcolor: alpha(marchio, 0.08), borderTop: `4px solid ${marchio}`, px: 2.5, pt: 1.5, pb: 2.5 }}>
        <Tooltip title={t('scheda.torna')}>
          <IconButton onClick={onTorna} aria-label={t('scheda.torna')} sx={{ width: 44, height: 44, ml: -1.25, mb: 1, color: tokens.color.graphite }}>
            <ArrowBack fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ boxShadow: `0 1px 0 ${alpha(marchio, 0.25)}`, borderRadius: '14px' }}>
            <LogoAzienda nome={azienda.nome} logoUrl={azienda.logo_url} colore={azienda.colore} dimensione={72} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: tokens.font.display,
                fontWeight: 600,
                fontSize: '1.45rem',
                lineHeight: 1.15,
                letterSpacing: '-0.015em',
                color: tokens.color.graphite,
                overflowWrap: 'anywhere',
                fontVariationSettings: '"SOFT" 30',
              }}
            >
              {azienda.nome}
            </Typography>
            {luogo && <Typography sx={{ fontSize: '0.85rem', color: tokens.color.ash, mt: 0.5 }}>{luogo}</Typography>}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.75, alignItems: 'center' }}>
          <TierBadge tier={azienda.livello} size="sm" />
          {daConsole && azienda.abbonamento && (
            <SoftChip label={t(`scheda.abbonamenti.${azienda.abbonamento}`)} tone={TONO_ABBONAMENTO[azienda.abbonamento]} />
          )}
          {daConsole && azienda.stato && azienda.stato !== 'attiva' && (
            <SoftChip label={t(`stato_singolare.${azienda.stato}`)} tone="error" />
          )}
        </Box>
      </Box>

      {/* I fatti */}
      <Box component="dl" sx={{ m: 0, px: 2.5, pt: 0.5, pb: 2 }}>
        <Fatto etichetta={t('scheda.iscritta')}>{azienda.iscritta_il ? formati.giorno(azienda.iscritta_il) : '—'}</Fatto>
        <Fatto etichetta={t('scheda.account')}>
          {azienda.account_totale === 0 ? (
            t('scheda.nessun_account')
          ) : (
            <Box>
              <Typography component="span" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', display: 'block', mb: 0.75 }}>
                {t('scheda.account_totale', { count: azienda.account_totale })}
              </Typography>
              <BarraDeiLivelli perLivello={perLivello} formati={formati} />
            </Box>
          )}
        </Fatto>
        <Fatto etichetta={t('scheda.persone_attive', { giorni })}>
          <Partecipazione attive={azienda.persone_attive} totale={azienda.account_totale} colore={COLORE_LIVELLO[azienda.livello]} formati={formati} />
        </Fatto>
        <Fatto
          etichetta={t('scheda.adesso')}
          // I nomi solo nella console: sono dati personali dei dipendenti delle aziende clienti.
          sotto={daConsole && azienda.persone_adesso > 0 && azienda.al_lavoro_adesso && azienda.al_lavoro_adesso.length > 0
            ? <ChiLavoraAdesso persone={azienda.al_lavoro_adesso} />
            : undefined}
        >
          {azienda.persone_adesso > 0 ? (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.9, fontWeight: 600 }}>
              <PallinoAdesso />
              {t('scheda.adesso_valore', { count: azienda.persone_adesso })}
            </Box>
          ) : (
            <Box component="span" sx={{ color: tokens.color.ash }}>{t('scheda.adesso_nessuno')}</Box>
          )}
        </Fatto>
        {daConsole && azienda.pagina_aperta && azienda.pagina_aperta.length > 0 && (
          <Fatto
            etichetta={t('scheda.pagina_aperta')}
            sotto={<ChiHaLaPaginaAperta persone={azienda.pagina_aperta} quando={(iso) => quando(iso) ?? iso} />}
          >
            <Box component="span" sx={{ fontWeight: 600 }}>{t('scheda.adesso_valore', { count: azienda.pagina_aperta.length })}</Box>
          </Fatto>
        )}
        <Fatto etichetta={t('scheda.ultimo_uso')}>{ultimoUso}</Fatto>
        {daConsole && azienda.posti_acquistati !== undefined && (
          <Fatto etichetta={t('scheda.posti')}>
            {azienda.posti_acquistati === null
              ? t('scheda.posti_senza_limite')
              : t('scheda.posti_valore', { usati: formati.numero(azienda.account_totale), acquistati: formati.numero(azienda.posti_acquistati) })}
          </Fatto>
        )}
        {daConsole && fornitori.length > 0 && (
          <Fatto etichetta={t('scheda.fornitore')}>
            <ElencoRiferimenti aziende={fornitori} onScegli={onScegliAzienda} />
          </Fatto>
        )}
        {daConsole && portataDa && (
          <Fatto etichetta={t('scheda.portata_da')}>
            <Typography component="span" sx={{ fontWeight: 600, display: 'block', mb: portataDa.azienda ? 0.5 : 0 }}>{portataDa.partner}</Typography>
            {portataDa.azienda && <ElencoRiferimenti aziende={[portataDa.azienda]} onScegli={onScegliAzienda} />}
          </Fatto>
        )}
        {daConsole && portate.length > 0 && (
          <Fatto etichetta={t('scheda.ha_portato')}>
            <Typography component="span" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>{t('scheda.rifornisce_quante', { count: portate.length })}</Typography>
            <ElencoRiferimenti aziende={portate} onScegli={onScegliAzienda} />
          </Fatto>
        )}
        {daConsole && clienti.length > 0 && (
          <Fatto etichetta={t('scheda.rifornisce')}>
            <Typography component="span" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>{t('scheda.rifornisce_quante', { count: clienti.length })}</Typography>
            <ElencoRiferimenti aziende={clienti} onScegli={onScegliAzienda} />
          </Fatto>
        )}
        {azienda.sedi.length > 0 && (
          <Fatto etichetta={t('scheda.sedi')}>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {azienda.sedi.map((s) => {
                const nome = s.nome ?? [s.citta, s.provincia ? `(${s.provincia})` : null].filter(Boolean).join(' ');
                const dettagli = [
                  s.nome ? [s.citta, s.provincia ? `(${s.provincia})` : null].filter(Boolean).join(' ') : null,
                  daConsole ? s.indirizzo : null,
                ].filter(Boolean).join(' · ');
                return (
                  <Box component="li" key={s.chiave}>
                    <ButtonBase
                      onClick={() => onVaiAllaSede?.(s.chiave)}
                      aria-label={t('scheda.vai_alla_sede', { sede: nome })}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        textAlign: 'left',
                        borderRadius: 1,
                        py: 0.6,
                        px: 0.75,
                        mx: -0.75,
                        minHeight: 40,
                        '&:hover': { bgcolor: tokens.color.bone },
                        '&.Mui-focusVisible': { outline: `2px solid ${tokens.color.graphite}`, outlineOffset: -2 },
                      }}
                    >
                      <Place sx={{ fontSize: 17, mt: '1px', color: s.principale ? COLORE_LIVELLO[azienda.livello] : tokens.color.mist }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, lineHeight: 1.35 }}>
                          {nome}
                          {s.principale && azienda.sedi.length > 1 && (
                            <Box component="span" sx={{ fontWeight: 400, color: tokens.color.ash }}>{` · ${t('scheda.sede_principale')}`}</Box>
                          )}
                        </Typography>
                        {dettagli && <Typography sx={{ fontSize: '0.75rem', color: tokens.color.ash, lineHeight: 1.4 }}>{dettagli}</Typography>}
                        {s.legale && <Typography sx={{ fontSize: '0.75rem', color: tokens.color.ash, lineHeight: 1.4 }}>{t('scheda.sede_legale')}</Typography>}
                        {s.posizione.precisione === 'comune' && (
                          <Typography sx={{ fontSize: '0.75rem', color: tokens.color.ash, lineHeight: 1.4 }}>{t('scheda.al_centro_del_comune')}</Typography>
                        )}
                      </Box>
                    </ButtonBase>
                  </Box>
                );
              })}
            </Box>
            {daConsole && !!azienda.sedi_senza_posizione && azienda.sedi_senza_posizione > 0 && (
              <Typography sx={{ fontSize: '0.75rem', color: tokens.color.ash, mt: 0.75 }}>
                {t('scheda.sedi_fuori', {
                  count: azienda.sedi_senza_posizione,
                  motivo: t(`riepilogo.motivi.${azienda.motivo_senza_posizione ?? 'posizione_non_calcolata'}`),
                })}
              </Typography>
            )}
          </Fatto>
        )}
        {daConsole && azienda.sedi.length === 0 && azienda.motivo_senza_posizione && (
          <Fatto etichetta={t('riepilogo.senza_posizione')}>{t(`riepilogo.motivi.${azienda.motivo_senza_posizione}`)}</Fatto>
        )}
      </Box>

      {daConsole && onApri && azienda.tenant_id && (
        <Box sx={{ px: 2.5, pb: 3 }}>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            endIcon={<OpenInNew fontSize="small" />}
            onClick={() => onApri(azienda.tenant_id as string)}
            sx={{ minHeight: 44, bgcolor: tokens.color.graphite, '&:hover': { bgcolor: '#000' }, textTransform: 'none', fontWeight: 600 }}
          >
            {t('scheda.apri')}
          </Button>
        </Box>
      )}
    </Box>
  );
};
